import { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useNetworkStatus } from '@/api'
import { useActor } from '@/auth/pin/phoneSession'
import { hasAnyRole, ROLES } from '@/auth/roles'
import { newId } from '@/lib/ids'
import { enqueueCommand, nextDailyCounter } from '@/offline'
import type { SiteUnit } from '@/offline/sitePack'
import { Btn, Cta, Footer, ItemRow, Row, Strip, V1Screen, WaitCta } from '@/features/gate/v1/kit'
import { C } from '@/features/gate/v1/style'
import { cropWord, kg, shortCode, tr } from '@/features/gate/v1/text'
import type { OpenRunPayload } from '../commands'
import '../commands'
import type { ProcessState } from '../projection'
import {
  allergenCleanNeeded,
  checkMachineLabelFor,
  checkOpenLotBag,
  plannedGrams,
  runCode,
} from '../model/rules'
import { cropIcon, k, StepIcon } from './icons'
import { machineChoices, type MachineChoice } from './model'
import {
  CallSheet,
  Hint,
  LeadBox,
  NextArrow,
  OkPill,
  PHeader,
  SaveError,
  saveFailed,
  stepLabel,
  tail2,
} from './ui'
import { PScanner, refusalText, WithSite } from './parts'

interface Lot {
  unit: SiteUnit
  qr_payload: string
}

/**
 * V1-F6-01 — रन खोलें: machine and step from one list, the machine's own label scanned (pilot:
 * the machine is identified before processing on it), one bag of the lot, check, open.
 */
export function V1OpenRun() {
  return <WithSite>{(state) => <OpenRun state={state} />}</WithSite>
}

function OpenRun({ state }: { state: ProcessState }) {
  const navigate = useNavigate()
  const actor = useActor()
  const { online } = useNetworkStatus()
  const [choice, setChoice] = useState<MachineChoice | null>(null)
  /** The picked machine's label, scanned at the machine (checked on the site list). */
  const [label, setLabel] = useState<string | null>(null)
  const [lots, setLots] = useState<Lot[]>([])
  const [scanning, setScanning] = useState(true)
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState<string | null>(null)
  const [call, setCall] = useState(false)
  const home = () => navigate('/m/process')

  // F6.1-a — कौन सी मशीन?
  if (!choice) {
    const choices = machineChoices(state)
    return (
      <V1Screen
        header={
          <PHeader onBack={home} icon="machine" title={tr('process.v1.pick.title')} step={1} />
        }
        footer={
          <Footer>
            <Row>
              <Btn icon="call" onClick={() => setCall(true)}>
                {tr('process.v1.pick.office')}
              </Btn>
            </Row>
            <WaitCta>{tr('process.v1.pick.wait')}</WaitCta>
          </Footer>
        }
        overlay={
          call ? (
            <CallSheet
              state={state}
              title={tr('process.v1.pick.office')}
              onClose={() => setCall(false)}
            />
          ) : undefined
        }
      >
        <Hint icon="eye">{tr('process.v1.pick.hint').replaceAll('**', '')}</Hint>
        {choices.length === 0 && <Strip icon="bang">{tr('process.v1.pick.none')}</Strip>}
        {choices.map((c) => (
          <ItemRow
            key={`${c.machine.id}:${c.step_code}`}
            padding="8px 10px"
            onClick={() =>
              c.busy
                ? navigate(`/m/process/run/${c.busy.id}`)
                : (setChoice(c), setLabel(null), setScanning(true))
            }
            lead={<LeadBox icon="machine" />}
            title={`${c.machine.name} · ${c.machine.code}`}
            line={
              <span style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                <StepIcon step={c.step_code} size={20} />
                {c.busy
                  ? tr('process.v1.pick.busy', { code: tail2(c.busy.code) })
                  : stepLabel(state, c.step_code)}
              </span>
            }
            right={<NextArrow color={c.busy ? C.muted : C.acc} />}
          />
        ))}
      </V1Screen>
    )
  }

  const origin = lots[0]?.unit ?? null
  const machineLine = `${choice.machine.code} · ${stepLabel(state, choice.step_code)}`

  // Pilot — मशीन का लेबल: only this machine's own bound label (offline, the site list).
  if (!label) {
    const canLabel = hasAnyRole(actor.roles, [
      ROLES.MACHINE_OPERATOR,
      ROLES.SUPERVISOR,
      ROLES.CENTRAL_ADMIN,
    ])
    const noLabel = !choice.machine.label_payload
    const onCode = (code: string) => {
      const v = checkMachineLabelFor(state, code, choice.machine)
      if (!v.ok)
        return v.reason === 'OTHER_MACHINE_LABEL'
          ? tr('process.refusal.OTHER_MACHINE_LABEL', { machine: v.detail ?? '' })
          : refusalText(v.reason)
      setLabel(v.value)
      setScanning(true)
      return null
    }
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => setChoice(null)}
            icon="machine"
            title={tr('process.v1.machineScan.title')}
            step={1}
          />
        }
        footer={
          <Footer>
            {noLabel && canLabel ? (
              <Cta
                label={tr('process.v1.machineScan.labelIt')}
                icon="tag"
                onClick={() => navigate('/m/process/machine-label')}
              />
            ) : (
              <WaitCta>
                {noLabel
                  ? tr('process.v1.machineScan.askSupervisor')
                  : tr('process.v1.machineScan.wait')}
              </WaitCta>
            )}
          </Footer>
        }
      >
        <ItemRow padding="6px 10px" lead={<LeadBox icon="machine" />} title={machineLine} />
        {noLabel ? (
          <>
            <Strip icon="bang">{tr('process.v1.machineScan.noLabel')}</Strip>
            {!canLabel && <Hint icon="call">{tr('process.v1.machineScan.askSupervisor')}</Hint>}
          </>
        ) : (
          <>
            <PScanner
              state={state}
              kind="MACHINE"
              icon="scan"
              hint={tr('process.v1.machineScan.hint')}
              onCode={onCode}
            />
            <Hint icon="machine">{tr('process.v1.machineScan.why')}</Hint>
          </>
        )}
      </V1Screen>
    )
  }

  // F6.1-c — लॉट की एक बोरी
  if (scanning) {
    const onCode = (code: string) => {
      const v = checkOpenLotBag(
        state,
        code,
        origin,
        lots.map((l) => l.unit.id),
      )
      if (!v.ok) return v.reason === 'DUPLICATE' ? null : refusalText(v.reason)
      setLots((ls) => [...ls, { unit: v.value, qr_payload: code }])
      setScanning(false)
      return null
    }
    return (
      <V1Screen
        header={
          <PHeader
            onBack={() => (lots.length ? setScanning(false) : setLabel(null))}
            icon="tag"
            title={tr('process.v1.lot.title')}
            step={1}
          />
        }
        footer={
          <Footer>
            <WaitCta>{tr('process.v1.lot.wait')}</WaitCta>
          </Footer>
        }
      >
        <ItemRow padding="6px 10px" lead={<LeadBox icon="machine" />} title={machineLine} />
        <PScanner state={state} kind="BAG" hint={tr('process.v1.lot.hint')} onCode={onCode} />
        <Hint icon="tag">{tr('process.v1.lot.stays')}</Hint>
      </V1Screen>
    )
  }

  // F6.1-d — जाँचें, रन खोलें
  const last = state.pack.last_runs.find((l) => l.equipment_id === choice.machine.id) ?? null
  const fullClean = origin
    ? allergenCleanNeeded({
        online,
        shared_use: choice.machine.shared_use,
        last,
        crop_id: origin.crop_id,
      })
    : false
  const planned = plannedGrams(
    state,
    lots.map((l) => l.unit.id),
  )

  const open = async () => {
    if (!origin) return
    setSaving(true)
    setErr(null)
    try {
      const runId = newId()
      const n = await nextDailyCounter(`run:${choice.machine.code}`)
      const payload: OpenRunPayload = {
        run_id: runId,
        code_proposed: runCode(choice.machine.code, n),
        equipment_id: choice.machine.id,
        // V1: picked from the list, then its own label scanned; no settings row, no flush.
        equipment_qr_payload: label,
        step_code: choice.step_code,
        lot_bag_qr_payloads: lots.map((l) => l.qr_payload),
        lot_unit_ids: lots.map((l) => l.unit.id),
        yield_band_id: null,
        flush_required: false,
        allergen_clean_required: fullClean,
        phone_online: online,
        previous_run_id: last?.run_id ?? null,
        planned_g: planned,
      }
      await enqueueCommand({ id: runId, type: 'open_run', payload, actorId: actor.user_id })
      navigate(`/m/process/run/${runId}`, { replace: true })
    } catch (e) {
      setErr(saveFailed(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <V1Screen
      header={
        <PHeader
          onBack={() => (setLots([]), setScanning(true))}
          icon="machine"
          title={tr('process.v1.check.title')}
          step={1}
        />
      }
      footer={
        <Footer>
          <SaveError text={err} />
          <Cta
            label={tr('process.v1.check.open')}
            icon="ok"
            busy={saving}
            onClick={() => void open()}
          />
        </Footer>
      }
    >
      {origin && (
        <ItemRow
          padding="8px 10px"
          lead={<LeadBox icon={cropIcon(origin.crop_id)} />}
          title={[cropWord(origin.crop_name), origin.farmer_code ? `#${origin.farmer_code}` : null]
            .filter(Boolean)
            .join(' · ')}
          line={
            origin.stream === 'MAIN' ? (
              <OkPill>{tr('process.v1.check.good')}</OkPill>
            ) : (
              <b style={{ color: C.bad }}>{tr('process.v1.check.red')}</b>
            )
          }
        />
      )}
      {lots.map((l) => (
        <ItemRow
          key={l.unit.id}
          padding="6px 10px"
          lead={<LeadBox icon="sack" />}
          title={tr('process.v1.check.lot', { code: shortCode(l.unit.code) })}
          right={<b style={{ fontSize: 17 }}>{kg(plannedGrams(state, [l.unit.id]))}</b>}
        />
      ))}
      <ItemRow
        padding="6px 10px"
        lead={<LeadBox icon="machine" />}
        title={machineLine}
        line={<OkPill>{tr('process.v1.machineScan.scanned')}</OkPill>}
      />
      {fullClean && (
        <Hint icon={k('broom')} tone="bad">
          {tr('process.v1.check.fullClean')}
        </Hint>
      )}
      <Row>
        <Btn icon={k('plus')} onClick={() => setScanning(true)}>
          {tr('process.v1.check.moreLots')}
        </Btn>
      </Row>
    </V1Screen>
  )
}
